import React, { useState, useEffect, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Alert,
  Snackbar,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Badge,
  DialogContentText
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadIcon from '@mui/icons-material/Upload';
import DicomGallery from './DicomGallery';
import UserDrawer from './UserDrawer';
import healthImagingService from '../services/HealthImagingService';
import { downloadStudyForViewing, cleanupAHICache } from '../utils/ahiImageLoader';
import { readDICOMFile } from '../utils/cornerstoneInit';
import './StudyList.css';

const StudyListAWS = ({ user, onLogout }) => {
  const [rows, setRows] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [nextToken, setNextToken] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0, phase: 'starting' });
  const [awsFiles, setAwsFiles] = useState([]);
  const [awsImageData, setAwsImageData] = useState([]);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [localFiles, setLocalFiles] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deidentifyDialogOpen, setDeidentifyDialogOpen] = useState(false);
  const [manualPhiDialogOpen, setManualPhiDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDeidentifyOption, setSelectedDeidentifyOption] = useState(null);
  const [phiTags, setPhiTags] = useState([]);
  const fileInputRef = useRef(null);
  const cloudFileInputRef = useRef(null);

  // Filter states
  const [filters, setFilters] = useState({
    patientId: '',
    patientName: '',
    studyDateFrom: '',
    studyDateTo: '',
    modality: ''
  });

  // Load studies on mount
  useEffect(() => {
    loadStudies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load studies from AWS HealthImaging
  const loadStudies = async (useNextToken = false) => {
    setLoading(true);
    setError(null);

    try {
      const searchParams = {
        ...filters,
        maxResults: pageSize,
        nextToken: useNextToken ? nextToken : undefined
      };

      const result = await healthImagingService.searchStudies(searchParams);

      console.log('Studies loaded:', result.studies);
      console.log('Number of studies:', result.studies.length);

      if (useNextToken) {
        setRows([...rows, ...result.studies]);
      } else {
        setRows(result.studies);
      }

      setNextToken(result.nextToken);
      setSuccessMessage(`Loaded ${result.studies.length} studies from AWS HealthImaging`);
    } catch (error) {
      console.error('Error loading studies:', error);
      setError(`Failed to load studies: ${error.message}`);

      // Fall back to dummy data if AWS fails
      if (error.message.includes('credentials') ||
        error.message.includes('UnauthorizedException') ||
        error.message.includes('DATASTORE_ID not configured') ||
        error.message.includes('AccessDeniedException')) {
        setError('AWS configuration issue. Showing demo data. Error: ' + error.message);
        loadDummyData();
      }
    } finally {
      setLoading(false);
    }
  };

  // Load dummy data for demo purposes
  const loadDummyData = () => {
    const dummyStudies = [];
    for (let i = 1; i <= 10; i++) {
      dummyStudies.push({
        id: `demo-${i}`,
        patientId: `DEMO${String(i).padStart(6, '0')}`,
        patientName: `Demo Patient ${i}`,
        studyDate: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        studyTime: '10:30',
        modality: ['CT', 'MR', 'US', 'XR'][Math.floor(Math.random() * 4)],
        studyDescription: 'Demo Study',
        accessionNumber: `ACC${String(i).padStart(6, '0')}`,
        numberOfImages: Math.floor(Math.random() * 100) + 1,
        studyInstanceUID: `1.2.3.4.5.demo.${i}`,
        status: 'ACTIVE',
        referringPhysician: 'Dr. Demo',
        imageSetVersion: '1',
        isDemo: true
      });
    }
    setRows(dummyStudies);
  };

  // Column definitions
  const columns = [
    {
      field: 'patientId',
      headerName: 'Patient ID',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
    },
    {
      field: 'patientName',
      headerName: 'Patient Name',
      flex: 1.2,
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'studyDate',
      headerName: 'Study Date',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
    },
    {
      field: 'studyTime',
      headerName: 'Time',
      flex: 0.5,
      minWidth: 60,
      sortable: true,
    },
    {
      field: 'modality',
      headerName: 'Modality',
      flex: 0.6,
      minWidth: 70,
      sortable: true,
    },
    {
      field: 'studyDescription',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'numberOfImages',
      headerName: 'Images',
      flex: 0.5,
      minWidth: 60,
      type: 'number',
      sortable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 80,
      sortable: true,
      cellClassName: (params) => {
        return params.value === 'ACTIVE' ? 'status-completed' : 'status-in-progress';
      },
    },
    {
      field: 'referringPhysician',
      headerName: 'Referring Physician',
      flex: 1.0,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.0,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Study">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleViewStudy(params.row);
              }}
              className="action-button-view"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!params.row.isDemo && (
            <Tooltip title="Download from AWS">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadStudy(params.row);
                }}
                sx={{ color: '#FF9800' }}
              >
                <CloudDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditStudy(params.row);
              }}
              className="action-button-edit"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteStudy(params.row);
              }}
              className="action-button-delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Handlers
  const handleRowClick = (params) => {
    handleViewStudy(params.row);
  };

  const handleViewStudy = async (study) => {
    console.log('Viewing study:', study);
    setSelectedStudy(study);
    setAwsFiles([]);
    setAwsImageData([]);

    // If it's not a demo study, download images from AWS
    if (!study.isDemo) {
      setDownloadingImages(true);
      setDownloadProgress({ current: 0, total: 0, phase: 'starting' });
      
      try {
        await downloadStudyForViewing(
          healthImagingService, 
          study.id, 
          (progress) => {
            setDownloadProgress(progress);
          },
          (batch) => {
            setAwsImageData(prevData => [...prevData, ...batch]);
          }
        );
        
        setSuccessMessage(`Successfully loaded images from AWS HealthImaging`);
      } catch (error) {
        console.error('Error downloading study images:', error);
        setError(`Failed to download images: ${error.message}`);
      } finally {
        setDownloadingImages(false);
      }
    }
  };

  const handleDownloadStudy = async (study) => {
    try {
      setSuccessMessage('Downloading study images...');
      const images = await healthImagingService.getStudyImages(study.id);

      // Download each image
      for (const image of images) {
        const url = await healthImagingService.downloadImageAsDicom(study.id, image.imageFrameId);
        // Create download link
        const a = document.createElement('a');
        a.href = url;
        a.download = `${study.patientId}_${image.sopInstanceUID}.dcm`;
        a.click();
        URL.revokeObjectURL(url);
      }

      setSuccessMessage(`Downloaded ${images.length} images`);
    } catch (error) {
      console.error('Error downloading study:', error);
      setError(`Failed to download: ${error.message}`);
    }
  };

  const handleEditStudy = (study) => {
    console.log('Editing study:', study);
    alert(`Edit functionality for ${study.patientName} - ${study.studyDescription}`);
  };

  const handleDeleteStudy = (study) => {
    if (window.confirm(`Delete study for ${study.patientName}?`)) {
      if (study.isDemo) {
        setRows(rows.filter(row => row.id !== study.id));
      } else {
        setError('Delete functionality not implemented for AWS studies');
      }
    }
  };

  const handleRefresh = () => {
    loadStudies();
  };

  const handleBackToList = () => {
    setSelectedStudy(null);
    setAwsFiles([]);
    setAwsImageData([]);
    cleanupAHICache();
  };

  const handleFilterSubmit = () => {
    setFilterDialogOpen(false);
    loadStudies();
  };

  // Handle local DICOM file selection
  const handleLocalFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    try {
      // Store the selected files
      setLocalFiles(files);

      // Create a local study object
      const localStudy = {
        id: `local-${Date.now()}`,
        patientId: 'LOCAL',
        patientName: 'Local Files',
        studyDate: new Date().toISOString().split('T')[0],
        studyTime: new Date().toTimeString().split(' ')[0].substring(0, 5),
        modality: 'LOCAL',
        studyDescription: `Local DICOM Files (${files.length} files)`,
        numberOfImages: files.length,
        status: 'ACTIVE',
        referringPhysician: 'Local',
        isDemo: false,
        isLocal: true, // Flag to identify local studies
        localFiles: files // Store the actual files
      };

      // Set this as the selected study and navigate to viewer
      setSelectedStudy(localStudy);
      setAwsFiles(files); // Pass files to DicomGallery
      setAwsImageData([]); // Clear AWS data
      
      setSuccessMessage(`Loaded ${files.length} local DICOM files`);
    } catch (error) {
      console.error('Error loading local files:', error);
      setError(`Failed to load local files: ${error.message}`);
    }

    // Clear the file input
    event.target.value = '';
  };

  // Handle upload dialog options
  const handleViewLocally = () => {
    setUploadDialogOpen(false);
    // Trigger file input for local viewing
    fileInputRef.current?.click();
  };

  const handleUploadToCloud = () => {
    setUploadDialogOpen(false);
    // Open deidentification dialog
    setDeidentifyDialogOpen(true);
  };

  // Handle cloud file selection
  const handleCloudFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setSelectedFiles(files);
    
    if (selectedDeidentifyOption === 'manual') {
      // Extract PHI tags from the first file for manual editing
      try {
        const firstFile = files[0];
        const dicomData = await readDICOMFile(firstFile);
        
        // Common PHI tags to display for editing
        const phiTagsToExtract = [
          { tag: 'x00100010', name: 'Patient Name', currentValue: dicomData.string('x00100010') || '' },
          { tag: 'x00100020', name: 'Patient ID', currentValue: dicomData.string('x00100020') || '' },
          { tag: 'x00100030', name: 'Patient Birth Date', currentValue: dicomData.string('x00100030') || '' },
          { tag: 'x00100040', name: 'Patient Sex', currentValue: dicomData.string('x00100040') || '' },
          { tag: 'x00080020', name: 'Study Date', currentValue: dicomData.string('x00080020') || '' },
          { tag: 'x00080030', name: 'Study Time', currentValue: dicomData.string('x00080030') || '' },
          { tag: 'x00080050', name: 'Accession Number', currentValue: dicomData.string('x00080050') || '' },
          { tag: 'x00080090', name: 'Referring Physician', currentValue: dicomData.string('x00080090') || '' },
          { tag: 'x00081030', name: 'Study Description', currentValue: dicomData.string('x00081030') || '' },
          { tag: 'x0008103e', name: 'Series Description', currentValue: dicomData.string('x0008103e') || '' },
          { tag: 'x00200010', name: 'Study ID', currentValue: dicomData.string('x00200010') || '' },
          { tag: 'x00081040', name: 'Institutional Department', currentValue: dicomData.string('x00081040') || '' },
          { tag: 'x00081070', name: 'Operators Name', currentValue: dicomData.string('x00081070') || '' },
          { tag: 'x00081080', name: 'Admitting Diagnoses Description', currentValue: dicomData.string('x00081080') || '' }
        ];

        // Add new values field for editing
        const phiTagsWithNewValues = phiTagsToExtract.map(tag => ({
          ...tag,
          newValue: tag.currentValue // Initialize with current value
        }));

        setPhiTags(phiTagsWithNewValues);
        setManualPhiDialogOpen(true);
      } catch (error) {
        console.error('Error reading DICOM file for PHI extraction:', error);
        setError(`Failed to read DICOM file: ${error.message}`);
      }
    } else {
      // For 'none' and 'automatic' options, proceed directly
      handleUploadWithFiles(files, selectedDeidentifyOption);
    }

    // Clear the file input
    event.target.value = '';
  };

  // Handle deidentification options
  const handleDeidentifyOption = (option) => {
    setSelectedDeidentifyOption(option);
    setDeidentifyDialogOpen(false);
    
    // Trigger file selection for cloud upload
    cloudFileInputRef.current?.click();
  };

  // Handle upload with files and deidentification option
  const handleUploadWithFiles = (files, deidentifyOption) => {
    // Placeholder for actual upload implementation
    console.log('Upload with files:', files.length, 'deidentification:', deidentifyOption);
    setError(`Cloud upload with ${files.length} files and ${deidentifyOption} deidentification will be implemented in a future task`);
  };

  // Handle manual PHI tag value changes
  const handlePhiTagChange = (index, newValue) => {
    setPhiTags(prev => prev.map((tag, i) => 
      i === index ? { ...tag, newValue } : tag
    ));
  };

  // Handle manual PHI submission
  const handleManualPhiSubmit = () => {
    setManualPhiDialogOpen(false);
    
    // Process the PHI tags and upload
    const modifiedTags = phiTags.filter(tag => tag.newValue !== tag.currentValue);
    console.log('Modified PHI tags:', modifiedTags);
    
    handleUploadWithFiles(selectedFiles, 'manual');
  };

  // If a study is selected, show the DICOM viewer
  if (selectedStudy) {
    return (
      <div className="viewer-container">
        <div className="study-list-header">
          <Box className="study-list-header-content">
            <Box className="viewer-header">
              <Button
                variant="outlined"
                onClick={handleBackToList}
                className="back-button"
              >
                ← Back to Study List
              </Button>
              <Typography className="study-info">
                {selectedStudy.patientName} - {selectedStudy.studyDescription}
              </Typography>
            </Box>
            <Typography className="study-meta">
              Study Date: {selectedStudy.studyDate} | {selectedStudy.numberOfImages} images |
              {selectedStudy.isDemo ? ' (Demo)' : ' (AWS)'}
            </Typography>
          </Box>
        </div>
        <Box className="viewer-content">
          {selectedStudy && !selectedStudy.isDemo && awsImageData.length === 0 && downloadingImages ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" p={4}>
              <CircularProgress size={60} />
              <Typography mt={3} variant="h6">
                {downloadProgress.phase === 'metadata' 
                  ? 'Fetching study information...' 
                  : 'Downloading images from AWS HealthImaging...'}
              </Typography>
              
              {downloadProgress.total > 0 && (
                <>
                  <Box width="100%" maxWidth={400} mt={3}>
                    <LinearProgress 
                      variant="determinate" 
                      value={downloadProgress.percentage || 0}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Typography mt={2} variant="body1">
                    {downloadProgress.message || `Loading image ${downloadProgress.current} of ${downloadProgress.total}`}
                  </Typography>
                  {downloadProgress.percentage && (
                    <Typography variant="body2" color="text.secondary">
                      {downloadProgress.percentage}% complete
                    </Typography>
                  )}
                </>
              )}
              
              {downloadProgress.phase === 'metadata' && (
                <Typography mt={1} color="text.secondary" variant="body2">
                  Connecting to AWS HealthImaging...
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <DicomGallery awsFiles={awsFiles} awsImageData={awsImageData} />
              {downloadingImages && awsImageData.length > 0 && (
                <Box 
                  position="absolute" 
                  bottom={20} 
                  right={20} 
                  bgcolor="background.paper" 
                  p={2} 
                  borderRadius={2}
                  boxShadow={3}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={24} />
                    <Typography variant="body2">
                      Loading {downloadProgress.current} of {downloadProgress.total}
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </div>
    );
  }

  // Show the study list
  return (
    <div className="study-list-container">
      <div className="study-list-header">
        <Box className="study-list-header-content">
          <Typography variant="h5" component="h1" className="study-list-title">
            POCUS CLOUD - Study List
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            {/* Action Buttons */}
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{ color: '#fff', borderColor: '#fff' }}
            >
              Upload Study
            </Button>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFilterDialogOpen(true)}
              sx={{ color: '#fff', borderColor: '#fff' }}
            >
              Filter
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              className="refresh-button"
              disabled={loading}
            >
              Refresh
            </Button>
            
            {/* User Info Section */}
            <Box display="flex" gap={1} alignItems="center" ml={2}>
              <Badge badgeContent={3} color="error">
                <IconButton sx={{ color: '#fff' }}>
                  <NotificationsIcon />
                </IconButton>
              </Badge>
              <Tooltip title="User Menu">
                <IconButton
                  onClick={() => setUserDrawerOpen(true)}
                  sx={{ 
                    color: '#fff',
                    display: 'flex',
                    gap: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'white',
                      color: '#1e3c72',
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                  <MenuIcon sx={{ ml: 1 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </div>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <Box className="grid-container">
        <Box className="grid-wrapper">
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            pagination
            loading={loading}
            disableSelectionOnClick
            onRowClick={handleRowClick}
            autoHeight={false}
            className="study-list-grid"
          />

          {nextToken && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                onClick={() => loadStudies(true)}
                disabled={loading}
              >
                Load More Studies
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Studies</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Patient ID"
              value={filters.patientId}
              onChange={(e) => setFilters({ ...filters, patientId: e.target.value })}
              fullWidth
            />
            <TextField
              label="Patient Name"
              value={filters.patientName}
              onChange={(e) => setFilters({ ...filters, patientName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Study Date From"
              type="date"
              value={filters.studyDateFrom}
              onChange={(e) => setFilters({ ...filters, studyDateFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Study Date To"
              type="date"
              value={filters.studyDateTo}
              onChange={(e) => setFilters({ ...filters, studyDateTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Modality</InputLabel>
              <Select
                value={filters.modality}
                onChange={(e) => setFilters({ ...filters, modality: e.target.value })}
                label="Modality"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CT">CT</MenuItem>
                <MenuItem value="MR">MR</MenuItem>
                <MenuItem value="US">US</MenuItem>
                <MenuItem value="XR">XR</MenuItem>
                <MenuItem value="DX">DX</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleFilterSubmit} variant="contained">Apply Filters</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Study Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Study</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose how you would like to handle your DICOM files:
          </DialogContentText>
          <Box display="flex" flexDirection="column" gap={2} mt={3}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleViewLocally}
              startIcon={<VisibilityIcon />}
              sx={{ 
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2
              }}
            >
              <Box>
                <Typography variant="subtitle1" component="div">
                  View Study Locally
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Load DICOM files for local viewing only. Files will not be uploaded to the cloud.
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleUploadToCloud}
              startIcon={<CloudUploadIcon />}
              sx={{ 
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2
              }}
            >
              <Box>
                <Typography variant="subtitle1" component="div">
                  Upload to Cloud Storage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload DICOM files to your AWS HealthImaging account for permanent storage.
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Deidentification Dialog */}
      <Dialog open={deidentifyDialogOpen} onClose={() => setDeidentifyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Study Deidentification Options</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose how you would like to handle patient health information (PHI) during upload:
          </DialogContentText>
          <Box display="flex" flexDirection="column" gap={2} mt={3}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDeidentifyOption('none')}
              startIcon={<UploadIcon />}
              sx={{ 
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2
              }}
            >
              <Box>
                <Typography variant="subtitle1" component="div">
                  No Deidentification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload DICOM files as-is without removing any patient health information. All original data will be preserved.
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDeidentifyOption('manual')}
              startIcon={<SettingsIcon />}
              sx={{ 
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2
              }}
            >
              <Box>
                <Typography variant="subtitle1" component="div">
                  Manual PHI Removal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You will specify which DICOM fields to remove. Common PHI fields like Patient Name, Patient ID, Study Date, etc. will be presented for selection. Includes pixel data PHI removal.
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDeidentifyOption('automatic')}
              startIcon={<PersonOffIcon />}
              sx={{ 
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2
              }}
            >
              <Box>
                <Typography variant="subtitle1" component="div">
                  Automatic PHI Removal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our backend processing will automatically remove all standard PHI fields from DICOM tags according to DICOM PS3.15 guidelines. Includes pixel data PHI removal.
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeidentifyDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* User Drawer */}
      <UserDrawer
        open={userDrawerOpen}
        onClose={() => setUserDrawerOpen(false)}
        user={user}
        onLogout={onLogout}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Manual PHI Removal Dialog */}
      <Dialog open={manualPhiDialogOpen} onClose={() => setManualPhiDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Manual PHI Removal - Edit DICOM Tags</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Review and modify the DICOM tag values below. Empty fields will remove the tag entirely. 
            Changes will be applied to all selected files.
          </DialogContentText>
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Files: {selectedFiles.length} file(s)
            </Typography>
            <Box display="flex" flexDirection="column" gap={2} mt={2} maxHeight="400px" overflow="auto">
              {phiTags.map((tag, index) => (
                <Box key={tag.tag} display="flex" gap={2} alignItems="center">
                  <Box minWidth="200px">
                    <Typography variant="body2" fontWeight="bold">
                      {tag.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tag.tag}
                    </Typography>
                  </Box>
                  <TextField
                    label="Current Value"
                    value={tag.currentValue}
                    disabled
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="New Value"
                    value={tag.newValue}
                    onChange={(e) => handlePhiTagChange(index, e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder="Leave empty to remove tag"
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualPhiDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleManualPhiSubmit} variant="contained">
            Apply Changes & Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Drawer */}
      <UserDrawer
        open={userDrawerOpen}
        onClose={() => setUserDrawerOpen(false)}
        user={user}
        onLogout={onLogout}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Hidden file input for local DICOM files */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".dcm"
        multiple
        onChange={handleLocalFileSelect}
        style={{ display: 'none' }}
      />

      {/* Hidden file input for cloud upload */}
      <input
        ref={cloudFileInputRef}
        type="file"
        accept=".dcm"
        multiple
        onChange={handleCloudFileSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default StudyListAWS;
